import React, { useCallback, useState } from 'react';
import { ChevronDown, ChevronUp, RefreshCw, AlertTriangle, BarChart3 } from 'lucide-react';
import * as YearEndService from '../../../../services/YearEndService';
import { formatSek } from '../constants';

/**
 * Granska resultat- och balansräkning — read-only ÅR-classified RR/BR with
 * jämförelseår, available from the avstämning step onward (P7 parity
 * finding: competitors surface RR/BR right after import; we surface them
 * from the live ledger). Corrections are made with verifikat — this panel
 * deliberately has no editing.
 */
/**
 * Vilket regelverk siffrorna är uppställda efter. Panelen visade tidigare
 * "(K2)" för alla bolagsformer — men ett handelsbolag upprättar årsbokslut
 * enligt BFNAR 2017:3 och lämnar ingen årsredovisning alls, och en enskild
 * firma med förenklat årsbokslut följer K1. Att kalla deras siffror
 * "årsredovisningen (K2)" är fel regelverk i klartext.
 */
function regelverkText(processType) {
    if (processType === 'hb_arsbokslut') {
        return 'uppställda som i årsbokslutet (BFNAR 2017:3)';
    }
    if (String(processType || '').startsWith('ef_')) {
        return 'uppställda som i årsbokslutet';
    }
    return 'uppställda som i årsredovisningen (K2)';
}

const StatementsPanel = ({ companyId, processId, processType }) => {
    const [open, setOpen] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchStatements = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const resp = await YearEndService.getStatements(companyId, processId);
            setData(resp?.data || null);
        } catch (err) {
            setError(err.response?.data?.message || 'Kunde inte hämta rapporterna.');
        } finally {
            setLoading(false);
        }
    }, [companyId, processId]);

    const toggle = () => {
        const next = !open;
        setOpen(next);
        if (next && !data && !loading) fetchStatements();
    };

    const hasPrev = Boolean(data?.previousFiscalYear);
    const rr = data?.resultatrakning;
    const br = data?.balansrakning;

    const numCell = (v) => (
        <td className="text-end ye-num">{v == null ? '—' : formatSek(v)}</td>
    );

    const renderRr = () => (
        <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
                <thead>
                    <tr>
                        <th>Resultaträkning</th>
                        <th className="text-end">{data.fiscalYear?.year}</th>
                        {hasPrev && <th className="text-end">{data.previousFiscalYear?.year}</th>}
                    </tr>
                </thead>
                <tbody>
                    {rr.sections.map((section) => (
                        <React.Fragment key={section.key}>
                            {section.isSummering ? (
                                <tr className="table-light fw-semibold">
                                    <td>{section.label}</td>
                                    {numCell(section.subtotal)}
                                    {hasPrev && numCell(section.prevSubtotal)}
                                </tr>
                            ) : (
                                <>
                                    <tr className="fw-semibold">
                                        <td colSpan={hasPrev ? 3 : 2}>{section.label}</td>
                                    </tr>
                                    {(section.rows || []).map((row) => (
                                        <tr key={row.key}>
                                            <td style={{ paddingLeft: '1.25rem' }}>{row.label}</td>
                                            {numCell(row.amount)}
                                            {hasPrev && numCell(row.prevAmount)}
                                        </tr>
                                    ))}
                                    {section.sumLabel && (
                                        <tr className="fw-semibold">
                                            <td>{section.sumLabel}</td>
                                            {numCell(section.subtotal)}
                                            {hasPrev && numCell(section.prevSubtotal)}
                                        </tr>
                                    )}
                                </>
                            )}
                        </React.Fragment>
                    ))}
                    <tr className="table-light fw-bold">
                        <td>Årets resultat</td>
                        {numCell(rr.aretsResultat)}
                        {hasPrev && numCell(rr.prevAretsResultat)}
                    </tr>
                </tbody>
            </table>
        </div>
    );

    const renderBrSide = (label, groups, total, prevTotal) => (
        <div className="table-responsive">
            <table className="table table-sm align-middle mb-0">
                <thead>
                    <tr>
                        <th>{label}</th>
                        <th className="text-end">{data.fiscalYear?.year}</th>
                        {hasPrev && <th className="text-end">{data.previousFiscalYear?.year}</th>}
                    </tr>
                </thead>
                <tbody>
                    {groups.map((group) => (
                        <React.Fragment key={group.label}>
                            {group.rows ? (
                                <>
                                    <tr className="fw-semibold">
                                        <td colSpan={hasPrev ? 3 : 2}>{group.label}</td>
                                    </tr>
                                    {group.rows.map((row) => (
                                        <tr key={row.label}>
                                            <td style={{ paddingLeft: '1.25rem' }}>{row.label}</td>
                                            {numCell(row.amount)}
                                            {hasPrev && numCell(row.prevAmount)}
                                        </tr>
                                    ))}
                                    <tr className="fw-semibold">
                                        <td>{group.sumLabel}</td>
                                        {numCell(group.subtotal)}
                                        {hasPrev && numCell(group.prevSubtotal)}
                                    </tr>
                                </>
                            ) : (
                                <tr>
                                    <td>{group.label}</td>
                                    {numCell(group.amount)}
                                    {hasPrev && numCell(group.prevAmount)}
                                </tr>
                            )}
                        </React.Fragment>
                    ))}
                    <tr className="table-light fw-bold">
                        <td>Summa</td>
                        {numCell(total)}
                        {hasPrev && numCell(prevTotal)}
                    </tr>
                </tbody>
            </table>
        </div>
    );

    return (
        <div className="modern-card" style={{ marginTop: '1rem' }}>
            <button
                type="button"
                className="ye-statements-toggle"
                onClick={toggle}
                aria-expanded={open}
            >
                <span className="d-inline-flex align-items-center gap-2">
                    <BarChart3 size={16} />
                    Granska resultat- och balansräkning
                </span>
                {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {open && (
                <div style={{ padding: '0 1.25rem 1.25rem' }}>
                    <p className="text-muted" style={{ fontSize: '0.8125rem', maxWidth: '68ch' }}>
                        Siffrorna hämtas direkt ur bokföringen, {regelverkText(processType)} med
                        jämförelseår. Vill du ändra något gör du det med ett verifikat —
                        här finns inget att skriva i, med flit.
                    </p>
                    {loading && (
                        <span className="d-inline-flex align-items-center gap-2 text-muted">
                            <RefreshCw size={14} className="ye-spin" /> Hämtar…
                        </span>
                    )}
                    {error && (
                        <div className="alert alert-warning d-flex align-items-center gap-2 mb-0">
                            <AlertTriangle size={16} />
                            <span>{error}</span>
                        </div>
                    )}
                    {data && rr && br && (
                        <>
                            <div className="ye-statements-grid">
                                {renderRr()}
                                <div>
                                    {renderBrSide('Tillgångar', br.assets, br.totalAssets, br.prevTotalAssets)}
                                    <div style={{ height: '1rem' }} />
                                    {renderBrSide(
                                        'Eget kapital och skulder',
                                        br.equityLiabilities,
                                        br.totalEquityAndLiabilities,
                                        br.prevTotalEquityAndLiabilities,
                                    )}
                                </div>
                            </div>
                            {/* C8 (2026-08-11): varje summa avrundas exakt för sig
                                (samma regel som årsredovisningen) — därför kan
                                delsummornas summa avvika 1 kr från totalen. */}
                            <p className="text-muted mb-0" style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>
                                Alla belopp i hela kronor, avrundade var för sig — delsummor kan
                                därför avvika en krona från totalen.
                            </p>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default StatementsPanel;
